import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { deepClone } from '@seahorse/common';
import { BypassSecurityTrustHtmlPipe } from '@seahorse/common';
import { MarkdownPreviewComponent } from '../markdown/markdown-preview.component';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CodeEditorService } from './code-editor.service';
import { NuMonacoEditorComponent } from '@ng-util/monaco-editor';

@Component({
  selector: 'sh-code-editor',
  templateUrl: './code-editor.component.html',
  imports: [
    CommonModule,
    FormsModule,
    BypassSecurityTrustHtmlPipe,
    MarkdownPreviewComponent,
    TranslateModule,
    NuMonacoEditorComponent,
  ],
})
export class CodeEditorComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(NuMonacoEditorComponent)
  monacoEditorComponent?: NuMonacoEditorComponent;

  @Input() height = '100px';
  @Input() isReadOnly = false;
  @Input() showDecorators = false;
  @Input() language = 'json'; // 'html', 'handlebars', 'json', 'xml', 'plaintext', etc. See https://microsoft.github.io/monaco-editor/

  private _code = '';

  @Input()
  get code(): string {
    return this._code;
  }
  set code(value: unknown) {
    value = value ?? '';

    if (value !== this._code) {
      this._code =
        typeof value === 'string' ? value : JSON.stringify(value, null, 2);

      this.codeChange.emit(this._code);
    }
  }

  @Output()
  codeChange = new EventEmitter<string>();

  get editorInstance() {
    return this.monacoEditorComponent?.editor;
  }

  editorOptions: any = {
    theme: 'vs',
    fontSize: 14,
  };

  private _subscription = new Subscription();
  private _codeEditorService = inject(CodeEditorService);

  ngOnInit() {
    this.editorOptions = deepClone({
      ...this.editorOptions,
      readOnly: this.isReadOnly,
      language: this.language,
      lineNumbers: this.showDecorators ? 'on' : 'off',
      minimap: { enabled: this.showDecorators },
    });

    this._codeEditorService.register(this);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isReadOnly']) {
      this.editorOptions = deepClone({
        ...this.editorOptions,
        readOnly: this.isReadOnly,
      });
    }

    if (changes['language']) {
      this.editorOptions = deepClone({
        ...this.editorOptions,
        language: this.language,
      });
    }

    if (changes['showDecorators']) {
      this.editorOptions = deepClone({
        ...this.editorOptions,
        lineNumbers: this.showDecorators ? 'on' : 'off',
        minimap: { enabled: this.showDecorators },
      });
    }
  }

  insertTextToEditor(text: string) {
    if (this.editorInstance) {
      const selection = this.editorInstance?.getSelection();

      if (selection) {
        this.editorInstance.executeEdits('', [
          {
            range: selection,
            text: text,
            forceMoveMarkers: true,
          },
        ]);
      }
      this.editorInstance.focus();
    }
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._codeEditorService.unregister();
  }
}
